/**
 * Normalizes reloaded AgentCore history into ordered chevron chat items
 * (`thinkingChevron` feature).
 *
 * History arrives as an envelope's `content[]` array whose *index* encodes the
 * agent's generation order, with `toolResult` blocks landing in a separate
 * `role: "user"` event — hence the two-step parse-then-correlate shape here.
 */

import { ChatItem, ToolCall } from "@/types";
import { ContentBlock } from "./types";
import { summarizeToolInput } from "./summarize";

/**
 * Converts one AgentCore message envelope's `content[]` into ordered chat items.
 *
 * Array order is preserved: a turn like
 * `[{text}, {toolUse}, {text}]` becomes `[text, tools, text]`, which is what
 * reproduces the agent's generation order on reload. Consecutive `toolUse`
 * blocks accumulate into a single `tools` item.
 *
 * `toolResult` blocks are *not* handled here — they arrive in a separate event
 * and are correlated later by `applyToolResults`.
 */
export function contentBlocksToChatItems(blocks: ContentBlock[]): ChatItem[] {
  const items: ChatItem[] = [];

  for (const block of blocks) {
    if (!block || typeof block !== "object") continue;

    if (block.toolUse) {
      const toolName = block.toolUse.name ?? "";
      if (!toolName) continue;

      const call: ToolCall = {
        toolUseId: block.toolUse.toolUseId,
        toolName,
        details: summarizeToolInput(toolName, block.toolUse.input),
        // Optimistic: a persisted tool call came from a finished turn. A
        // matching toolResult (if any) refines this via applyToolResults.
        status: "success",
      };

      const last = items[items.length - 1];
      if (last && last.kind === "tools") {
        last.calls.push(call);
      } else {
        items.push({ kind: "tools", calls: [call] });
      }
      continue;
    }

    if (typeof block.text === "string" && block.text.length > 0) {
      items.push({ kind: "text", content: block.text });
    }
  }

  return items;
}

/** Extracts `toolUseId → status` pairs from a message envelope's content. */
export function extractToolResults(
  blocks: ContentBlock[],
): Map<string, "success" | "error"> {
  const results = new Map<string, "success" | "error">();

  for (const block of blocks) {
    const result = block?.toolResult;
    if (!result?.toolUseId) continue;
    results.set(
      result.toolUseId,
      result.status === "error" ? "error" : "success",
    );
  }

  return results;
}

/**
 * Applies tool results onto previously-parsed chat items, matching on
 * `toolUseId`.
 *
 * `toolResult` blocks live in a *different* event from their `toolUse` (the
 * agent emits results as a follow-up `role: "user"` message), so correlation
 * has to happen across events. Matching on `toolUseId` — rather than arrival
 * order — keeps parallel tool calls correctly attributed.
 *
 * Mutates the items in place and returns them for convenience.
 */
export function applyToolResults(
  items: ChatItem[],
  results: Map<string, "success" | "error">,
): ChatItem[] {
  if (results.size === 0) return items;

  for (const item of items) {
    if (item.kind !== "tools") continue;
    for (const call of item.calls) {
      if (!call.toolUseId) continue;
      const status = results.get(call.toolUseId);
      if (status) call.status = status;
    }
  }

  return items;
}
